import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { webhookApi, WebhookFieldMapping } from '@/services/webhook';
import { useToast } from '@/hooks/use-toast';

export function useWebhookConfig() {
  return useQuery({
    queryKey: ['webhook-config'],
    queryFn: () => webhookApi.getConfig(),
    staleTime: 5 * 60 * 1000,
  });
}

export function useCreateWebhookConfig() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (data: { platform?: string }) =>
      webhookApi.createConfig(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['webhook-config'] });
      toast({
        title: 'Webhook configured',
        description: 'Your webhook URL has been generated. Copy it to your WordPress form settings.',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Failed to create webhook config',
        description: error?.message || 'An error occurred.',
        variant: 'destructive',
      });
    },
  });
}

export function useUpdateWebhookConfig() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (data: { platform?: string; isActive?: boolean }) =>
      webhookApi.updateConfig(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['webhook-config'] });
      toast({ title: 'Webhook config updated' });
    },
    onError: (error: any) => {
      toast({
        title: 'Failed to update config',
        description: error?.message || 'An error occurred.',
        variant: 'destructive',
      });
    },
  });
}

export function useRegenerateSecret() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: () => webhookApi.regenerateSecret(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['webhook-config'] });
      toast({
        title: 'Secret regenerated',
        description: 'Update your WordPress webhook URL within 24 hours.',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Failed to regenerate secret',
        description: error?.message || 'An error occurred.',
        variant: 'destructive',
      });
    },
  });
}

export function useFieldMappings() {
  return useQuery({
    queryKey: ['webhook-field-mappings'],
    queryFn: () => webhookApi.getFieldMappings(),
    staleTime: 5 * 60 * 1000,
  });
}

// Sprint 44 — mapping edits persist immediately (no Save button), so the
// mutations below update the query cache optimistically: the row appears /
// changes the instant the user acts, rolls back on error, and reconciles
// with the server via invalidation once the request settles.
export function useUpdateFieldMappings() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (
      mappings: Array<{ sourceField: string; targetField: string }>,
    ) => webhookApi.updateFieldMappings(mappings),
    onMutate: async (items) => {
      await queryClient.cancelQueries({ queryKey: ['webhook-field-mappings'] });
      const previous = queryClient.getQueryData<WebhookFieldMapping[]>([
        'webhook-field-mappings',
      ]);
      queryClient.setQueryData<WebhookFieldMapping[]>(
        ['webhook-field-mappings'],
        (old = []) => {
          const next = [...old];
          for (const item of items) {
            const idx = next.findIndex(
              (m) => m.sourceField === item.sourceField,
            );
            if (idx >= 0) {
              next[idx] = {
                ...next[idx],
                targetField: item.targetField,
                isAutoMapped: false,
                confidence: undefined,
              };
            } else {
              const now = new Date().toISOString();
              next.push({
                id: `optimistic-${item.sourceField}`,
                sourceField: item.sourceField,
                targetField: item.targetField,
                isAutoMapped: false,
                createdAt: now,
                updatedAt: now,
              });
            }
          }
          return next;
        },
      );
      return { previous };
    },
    onSuccess: () => {
      toast({ title: 'Field mapping saved' });
    },
    onError: (error: any, _items, context) => {
      if (context?.previous) {
        queryClient.setQueryData(['webhook-field-mappings'], context.previous);
      }
      toast({
        title: 'Failed to save mapping',
        description: error?.message || 'An error occurred.',
        variant: 'destructive',
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['webhook-field-mappings'] });
    },
  });
}

export function useDeleteFieldMapping() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (mappingId: string) =>
      webhookApi.deleteFieldMapping(mappingId),
    onMutate: async (mappingId) => {
      await queryClient.cancelQueries({ queryKey: ['webhook-field-mappings'] });
      const previous = queryClient.getQueryData<WebhookFieldMapping[]>([
        'webhook-field-mappings',
      ]);
      queryClient.setQueryData<WebhookFieldMapping[]>(
        ['webhook-field-mappings'],
        (old = []) => old.filter((m) => m.id !== mappingId),
      );
      return { previous };
    },
    onError: (_error, _mappingId, context) => {
      if (context?.previous) {
        queryClient.setQueryData(['webhook-field-mappings'], context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['webhook-field-mappings'] });
    },
  });
}

export function useRefreshFieldMappings() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: () => webhookApi.refreshFieldMappings(),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['webhook-field-mappings'] });
      const n = res.newlyMappedFields?.length ?? 0;
      toast({
        title: n > 0
          ? `${n} new field${n === 1 ? '' : 's'} detected`
          : 'Field mappings up to date',
        description: n > 0
          ? `Connected: ${res.newlyMappedFields.join(', ')}. Review the mappings below — changes save automatically.`
          : `Scanned ${res.scannedDeliveries} recent submission${res.scannedDeliveries === 1 ? '' : 's'} — no new fields found.`,
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Refresh failed',
        description: error?.message || 'An error occurred.',
        variant: 'destructive',
      });
    },
  });
}

export function useDeliveryLogs(page = 1, limit = 20) {
  return useQuery({
    queryKey: ['webhook-delivery-logs', page, limit],
    queryFn: () => webhookApi.getDeliveryLogs(page, limit),
    staleTime: 30 * 1000, // 30 seconds
  });
}

export function useTestWebhook() {
  const { toast } = useToast();

  return useMutation({
    mutationFn: (payload: Record<string, any>) =>
      webhookApi.testWebhook(payload),
    onSuccess: () => {
      toast({ title: 'Test completed', description: 'See the preview below.' });
    },
    onError: (error: any) => {
      toast({
        title: 'Test failed',
        description: error?.message || 'An error occurred.',
        variant: 'destructive',
      });
    },
  });
}
